import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const ACCESS_TOKEN_KEY = 'vision_chama_access_token';
const REFRESH_TOKEN_KEY = 'vision_chama_refresh_token';
const TOKEN_STORAGE_KEY = 'vision_chama_token_storage';

type StorageType = 'local' | 'session';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'https://chama.gritagencies.co.ke/api';

const requestConfig: AxiosRequestConfig = {
  baseURL: API_BASE_URL,
  timeout: 15000,
};

const apiClient: AxiosInstance = axios.create(requestConfig);
const unauthenticatedClient: AxiosInstance = axios.create(requestConfig);

const isBrowser = typeof window !== 'undefined';

const ensureHeaders = (headers?: AxiosRequestConfig['headers']): AxiosHeaders => {
  if (headers instanceof AxiosHeaders) {
    return headers;
  }

  return AxiosHeaders.from((headers ?? {}) as any) as AxiosHeaders;
};

const getPrimaryStorage = (storageType: StorageType) => {
  if (!isBrowser) {
    return null;
  }

  return storageType === 'local' ? window.localStorage : window.sessionStorage;
};

const getSecondaryStorage = (storageType: StorageType) => {
  if (!isBrowser) {
    return null;
  }

  return storageType === 'local' ? window.sessionStorage : window.localStorage;
};

const getStoredToken = (key: string): string | null => {
  if (!isBrowser) {
    return null;
  }

  return window.localStorage.getItem(key) ?? window.sessionStorage.getItem(key);
};

const setStoredToken = (key: string, value: string, storageType: StorageType) => {
  const primary = getPrimaryStorage(storageType);
  const secondary = getSecondaryStorage(storageType);

  primary?.setItem(key, value);
  secondary?.removeItem(key);
};

const clearStoredKey = (key: string) => {
  if (!isBrowser) {
    return;
  }

  window.localStorage.removeItem(key);
  window.sessionStorage.removeItem(key);
};

const getPersistedStorageType = (): StorageType => {
  if (!isBrowser) {
    return 'session';
  }

  return (
    (window.localStorage.getItem(TOKEN_STORAGE_KEY) as StorageType | null) ??
    (window.sessionStorage.getItem(TOKEN_STORAGE_KEY) as StorageType | null) ??
    'session'
  );
};

const setPersistedStorageType = (storageType: StorageType) => {
  const primary = getPrimaryStorage(storageType);
  const secondary = getSecondaryStorage(storageType);

  primary?.setItem(TOKEN_STORAGE_KEY, storageType);
  secondary?.removeItem(TOKEN_STORAGE_KEY);
};

export const tokenStorage = {
  getAccessToken: () => getStoredToken(ACCESS_TOKEN_KEY),
  getRefreshToken: () => getStoredToken(REFRESH_TOKEN_KEY),
  setTokens: (accessToken: string, refreshToken: string, remember = true) => {
    const storageType: StorageType = remember ? 'local' : 'session';
    setStoredToken(ACCESS_TOKEN_KEY, accessToken, storageType);
    setStoredToken(REFRESH_TOKEN_KEY, refreshToken, storageType);
    setPersistedStorageType(storageType);
  },
  setAccessToken: (accessToken: string) => {
    const storageType = getPersistedStorageType();
    setStoredToken(ACCESS_TOKEN_KEY, accessToken, storageType);
  },
  clear: () => {
    clearStoredKey(ACCESS_TOKEN_KEY);
    clearStoredKey(REFRESH_TOKEN_KEY);
    clearStoredKey(TOKEN_STORAGE_KEY);
  },
};

let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

const refreshAccessToken = async (): Promise<string> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const refreshToken = tokenStorage.getRefreshToken();

  if (!refreshToken) {
    tokenStorage.clear();
    throw new Error('Missing refresh token');
  }

  isRefreshing = true;
  refreshPromise = unauthenticatedClient
    .post(
      '/auth/refresh',
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      },
    )
    .then((response: AxiosResponse<{ access_token: string }>) => {
      const newAccessToken = response.data.access_token;
      tokenStorage.setAccessToken(newAccessToken);
      return newAccessToken;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
};

apiClient.interceptors.request.use((config) => {
  const token = tokenStorage.getAccessToken();

  if (token) {
    const headers = ensureHeaders(config.headers);
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const originalRequest = error.config;

    if (!originalRequest) {
      return Promise.reject(error);
    }

    const headers = ensureHeaders(originalRequest.headers);

    if (status === 401 && !headers.has('x-retry')) {
      try {
        const newAccessToken = await refreshAccessToken();
        headers.set('Authorization', `Bearer ${newAccessToken}`);
        headers.set('x-retry', 'true');
        originalRequest.headers = headers;
        return apiClient(originalRequest);
      } catch (refreshError) {
        tokenStorage.clear();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export { apiClient, unauthenticatedClient, API_BASE_URL };

 